import { ApplicationConfig, ErrorHandler, provideBrowserGlobalErrorListeners, provideZonelessChangeDetection, APP_INITIALIZER } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { MsalService, MsalBroadcastService, MSAL_INSTANCE } from '@azure/msal-angular';
import { PublicClientApplication, IPublicClientApplication, Configuration, LogLevel } from '@azure/msal-browser';
import { NGX_MONACO_EDITOR_CONFIG, NgxMonacoEditorConfig } from 'ngx-monaco-editor-v2';
import { provideEchartsCore } from 'ngx-echarts';
import * as echarts from 'echarts/core';
import { BarChart, LineChart, PieChart, ScatterChart, GaugeChart } from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  DatasetComponent,
  TransformComponent,
  ToolboxComponent,
  DataZoomComponent,
  VisualMapComponent
} from 'echarts/components';
import { LabelLayout, UniversalTransition } from 'echarts/features';
import { CanvasRenderer } from 'echarts/renderers';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { tokenInterceptor } from './auth/token.interceptor';
import { NeoGlobalErrorHandler } from './handlers/neo-global-error.handler';

// Register ECharts components for treeshaking
echarts.use([
  // Chart types
  BarChart,
  LineChart,
  PieChart,
  ScatterChart,
  GaugeChart,
  // Components
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  DatasetComponent,
  TransformComponent,
  ToolboxComponent,
  DataZoomComponent,
  VisualMapComponent,
  // Features
  LabelLayout,
  UniversalTransition,
  // Renderer
  CanvasRenderer
]);

function MSALInstanceFactory(): IPublicClientApplication {
  const msalConfig: Configuration = {
    auth: {
      clientId: environment.azureAdB2C.clientId,
      authority: environment.azureAdB2C.authority,
      knownAuthorities: environment.azureAdB2C.knownAuthorities,
      redirectUri: environment.azureAdB2C.redirectUri,
      postLogoutRedirectUri: environment.azureAdB2C.postLogoutRedirectUri,
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: false,
    },
    system: {
      loggerOptions: {
        loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
          if (containsPii) {
            return;
          }
          switch (level) {
            case LogLevel.Error:
              console.error('MSAL Error:', message);
              break;
            case LogLevel.Warning:
              console.warn('MSAL Warning:', message);
              break;
            case LogLevel.Info:
              console.log('MSAL Info:', message);
              break;
            case LogLevel.Verbose:
            case LogLevel.Trace:
              console.debug('MSAL Debug:', message);
              break;
          }
        },
        logLevel: environment.production ? LogLevel.Warning : LogLevel.Info,
      },
      // Configure iframe timeouts for better security and performance
      iframeHashTimeout: 6000, // Timeout for iframe hash resolution
      loadFrameTimeout: 6000, // Timeout for loading frames
    },
  };

  return new PublicClientApplication(msalConfig);
}

/**
 * APP_INITIALIZER for MSAL
 *
 * This function only initializes MSAL and handles redirect promises.
 * It does NOT check authentication state or initialize the auth service.
 *
 * The actual authentication state check happens via AppStateService
 * which waits for MSAL to broadcast InteractionStatus.None before
 * checking accounts. This prevents race conditions.
 */
function initializeMsal(msalService: MsalService) {
  return async () => {
    console.log('APP_INITIALIZER: Initializing MSAL...');
    await msalService.instance.initialize();
    console.log('APP_INITIALIZER: MSAL initialized successfully');

    // Handle redirect promise (required for redirect flows to work)
    const response = await msalService.instance.handleRedirectPromise();
    console.log('APP_INITIALIZER: Redirect promise handled', response ? 'with response' : 'no response');

    // IMPORTANT: Mark MSAL as ready AFTER handleRedirectPromise completes
    // This signals to AppStateService that MSAL redirect processing is complete
    // and it doesn't need to wait for inProgress$ to become None
    sessionStorage.setItem('neo-genesis-msal-ready', 'true');
    console.log('APP_INITIALIZER: MSAL ready flag set in sessionStorage');

    // If we have a successful login response, mark that we need to redirect
    // The actual redirect will happen after AppStateService completes initialization
    if (response && response.account) {
      console.log('APP_INITIALIZER: User logged in successfully', response.account.username);

      // Set the flag for post-login redirect
      sessionStorage.setItem('needsPostLoginRedirect', 'true');

      // If no specific redirect path was stored, set default to dashboard
      const storedPath = sessionStorage.getItem('postLoginRedirectPath');
      if (!storedPath) {
        console.log('APP_INITIALIZER: No stored redirect path, setting default to dashboard');
        sessionStorage.setItem('postLoginRedirectPath', '/restricted/dashboard');
      }
    }

    // NOTE: Do NOT call authService.checkAuthenticationState() here!
    // AppStateService handles this after checking the msal-ready flag
    console.log('APP_INITIALIZER: Complete. AppStateService will handle auth state check.');
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    { provide: ErrorHandler, useClass: NeoGlobalErrorHandler },
    provideRouter(routes),
    provideHttpClient(withInterceptors([tokenInterceptor])),
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    MsalService,
    MsalBroadcastService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeMsal,
      deps: [MsalService],
      multi: true
    },
    {
      provide: NGX_MONACO_EDITOR_CONFIG,
      useValue: {
        baseUrl: 'assets/monaco/min/vs',
        defaultOptions: {
          scrollBeyondLastLine: false,
          automaticLayout: true,
          minimap: { enabled: false }
        }
      } as NgxMonacoEditorConfig
    },
    // ECharts configuration with treeshaking
    provideEchartsCore({ echarts })
  ]
};
